import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  ClipboardList, ChevronLeft, ChevronRight, RefreshCw, ShieldCheck,
  ShieldOff, Ban, RotateCcw, Star, Flag, XCircle, Search,
  Download, Calendar, ChevronDown, UserCheck, AlertTriangle, Settings,
  X, Eye, ArrowUpRight, FileText, Check, FileSpreadsheet
} from "lucide-react";
import AdminService from "../../sevices/admin.service";

const TIMELINE_OPTIONS = [
  { id: "1d", label: "Today" },
  { id: "7d", label: "Last 7 Days" },
  { id: "1m", label: "Last 30 Days" },
  { id: "1y", label: "Last Year" },
  { id: "lifetime", label: "Lifetime" },
];

const isDateInRange = (dateVal, rangeValue) => {
  if (!dateVal || rangeValue === "lifetime") return true;
  let d;
  if (Array.isArray(dateVal)) {
    const [y, mo, day, h = 0, mi = 0] = dateVal;
    d = new Date(y, mo - 1, day, h, mi);
  } else {
    d = new Date(dateVal);
  }
  if (isNaN(d.getTime())) return true;

  const now = new Date();
  if (rangeValue === "1d") {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return d.getTime() >= startOfToday;
  }

  const diffTime = now.getTime() - d.getTime();
  const diffDays = diffTime / (1000 * 3600 * 24);

  if (rangeValue === "7d") return diffDays <= 7;
  if (rangeValue === "1m") return diffDays <= 30;
  if (rangeValue === "1y") return diffDays <= 365;
  return true;
};

const CATEGORY_TABS = [
  { id: "ALL", label: "All Activities", color: "purple" },
  { id: "ADMIN", label: "Admin Actions", color: "emerald" },
  { id: "USER", label: "User Actions", color: "amber" },
  { id: "SECURITY", label: "Security Events", color: "rose" },
  { id: "SYSTEM", label: "System Events", color: "blue" },
];

const ACTION_OPTIONS = [
  { value: "ALL", label: "All Actions" },
  { value: "VERIFY_IDENTITY", label: "Approved Verification" },
  { value: "REJECT_VERIFICATION", label: "Rejected Verification" },
  { value: "BAN_USER", label: "Banned User" },
  { value: "UNBAN_USER", label: "Unbanned User" },
  { value: "RESOLVE_REPORT", label: "Deleted Report" },
  { value: "ADJUST_CREDITS", label: "Adjusted Credits" },
  { value: "SYSTEM_BACKUP", label: "System Backup" },
  { value: "SYSTEM_MAINTENANCE", label: "System Maintenance" },
];

const SORT_OPTIONS = [
  { value: "NEWEST", label: "Sort by: Newest First" },
  { value: "OLDEST", label: "Sort by: Oldest First" },
];

const ACTION_CONFIG = {
  VERIFY_IDENTITY: { label: "Approved Verification", category: "USER", color: "bg-emerald-50 text-emerald-700 border-emerald-200/80", icon: ShieldCheck },
  REMOVE_VERIFICATION: { label: "Verification Removed", category: "USER", color: "bg-gray-100 text-gray-700 border-gray-200", icon: ShieldOff },
  REJECT_VERIFICATION: { label: "Rejected Verification", category: "SECURITY", color: "bg-rose-50 text-rose-700 border-rose-200/80", icon: XCircle },
  BAN_USER: { label: "Banned User", category: "SECURITY", color: "bg-rose-50 text-rose-700 border-rose-200/80", icon: Ban },
  UNBAN_USER: { label: "Unbanned User", category: "USER", color: "bg-emerald-50 text-emerald-700 border-emerald-200/80", icon: RotateCcw },
  ADJUST_CREDITS: { label: "Adjusted Credits", category: "ADMIN", color: "bg-purple-50 text-purple-700 border-purple-200/80", icon: Star },
  RESOLVE_REPORT: { label: "Deleted Report", category: "ADMIN", color: "bg-amber-50 text-amber-700 border-amber-200/80", icon: Flag },
  UPDATE_ROLE: { label: "Updated User Role", category: "ADMIN", color: "bg-purple-50 text-purple-700 border-purple-200/80", icon: UserCheck },
  SYSTEM_BACKUP: { label: "System Backup", category: "SYSTEM", color: "bg-blue-50 text-blue-700 border-blue-200/80", icon: Settings },
  SYSTEM_MAINTENANCE: { label: "System Maintenance", category: "SYSTEM", color: "bg-blue-50 text-blue-700 border-blue-200/80", icon: Settings },
  DATABASE_OPTIMIZE: { label: "Database Optimization", category: "SYSTEM", color: "bg-blue-50 text-blue-700 border-blue-200/80", icon: Settings },
  SECURITY_AUDIT: { label: "Security Audit", category: "SYSTEM", color: "bg-blue-50 text-blue-700 border-blue-200/80", icon: Settings },
  EDIT_PROFILE: { label: "Edited User Profile", category: "ADMIN", color: "bg-indigo-50 text-indigo-700 border-indigo-200/80", icon: FileText },
  SUSPEND_USER: { label: "Suspended User", category: "SECURITY", color: "bg-rose-50 text-rose-700 border-rose-200/80", icon: Ban },
};

// Default fallback logs including System Events
const DUMMY_SYSTEM_LOGS = [
  { id: 101, action: "SYSTEM_BACKUP", targetUserEmail: null, detail: "Daily database backup completed successfully", createdAt: new Date(Date.now() - 3600000).toISOString() },
  { id: 102, action: "SYSTEM_MAINTENANCE", targetUserEmail: null, detail: "FCM Push Service Health Check & Cache Flushing", createdAt: new Date(Date.now() - 86400000 * 3).toISOString() },
  { id: 103, action: "DATABASE_OPTIMIZE", targetUserEmail: null, detail: "Re-indexed user query tables & refreshed active sessions", createdAt: new Date(Date.now() - 86400000 * 15).toISOString() },
  { id: 104, action: "SECURITY_AUDIT", targetUserEmail: null, detail: "Automated JWT Auth & WebSocket security scan passed", createdAt: new Date(Date.now() - 86400000 * 60).toISOString() },
];

// Custom Popover Select Dropdown
const CustomSelectDropdown = ({ options, value, onChange, placeholder = "Select...", fullWidth = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOpt = options.find((o) => o.value === value) || options[0];

  return (
    <div className={`relative ${fullWidth ? "w-full" : ""}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between gap-3 px-4 py-3 bg-white border border-gray-200 rounded-2xl text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-2xs transition-colors cursor-pointer ${fullWidth ? "w-full" : ""
          }`}
      >
        <span>{selectedOpt?.label || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180 text-purple-600" : ""}`} />
      </button>

      {isOpen && (
        <div className={`absolute ${fullWidth ? "inset-x-0" : "right-0"} mt-2 min-w-[200px] bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150 max-h-60 overflow-y-auto`}>
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 text-xs sm:text-sm font-medium flex items-center justify-between transition-colors cursor-pointer ${isSelected ? "bg-purple-50 text-purple-700 font-bold" : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <span>{opt.label}</span>
                {isSelected && <Check className="w-4 h-4 text-purple-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Interactive Export Modal
const ActivityLogExportModal = ({ isOpen, onClose, logs }) => {
  const [selectedDuration, setSelectedDuration] = useState("7d");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [selectedFormat, setSelectedFormat] = useState("CSV");

  if (!isOpen) return null;

  const exportFilteredData = logs.filter((log) => {
    const meta = ACTION_CONFIG[log.action] || {};
    if (selectedCategory !== "ALL") {
      if (selectedCategory === "ADMIN" && meta.category !== "ADMIN" && meta.category !== undefined) return false;
      if (selectedCategory === "USER" && meta.category !== "USER") return false;
      if (selectedCategory === "SECURITY" && meta.category !== "SECURITY") return false;
      if (selectedCategory === "SYSTEM" && meta.category !== "SYSTEM") return false;
    }
    return true;
  });

  const handleDownload = () => {
    const headers = ["Activity ID", "Timestamp", "Category", "Action", "Target User", "Details"];
    const rows = exportFilteredData.map((l) => [
      `ACT-${l.id}`,
      `"${new Date(l.createdAt || Date.now()).toLocaleString()}"`,
      ACTION_CONFIG[l.action]?.category || "ADMIN",
      `"${ACTION_CONFIG[l.action]?.label || l.action}"`,
      `"${l.targetUserEmail || "System"}"`,
      `"${(l.detail || "").replace(/"/g, '""')}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Signova_Activity_Logs_${selectedDuration}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-gray-100 relative space-y-6">
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Export Activity Logs</h3>
              <p className="text-xs text-gray-400">Configure export parameters & download report.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-50 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Timeline Selection */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Duration Period</label>
            <CustomSelectDropdown
              options={TIMELINE_OPTIONS.map(t => ({ value: t.id, label: t.label }))}
              value={selectedDuration}
              onChange={(val) => setSelectedDuration(val)}
              fullWidth={true}
            />
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Activity Category</label>
            <CustomSelectDropdown
              options={CATEGORY_TABS.map(c => ({ value: c.id, label: c.label }))}
              value={selectedCategory}
              onChange={(val) => setSelectedCategory(val)}
              fullWidth={true}
            />
          </div>

          {/* Export File Format */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">File Format</label>
            <CustomSelectDropdown
              options={[
                { value: "CSV", label: "CSV Spreadsheet (.csv)" },
                { value: "EXCEL", label: "Microsoft Excel (.xlsx)" },
              ]}
              value={selectedFormat}
              onChange={(val) => setSelectedFormat(val)}
              fullWidth={true}
            />
          </div>
        </div>

        <div className="p-4 bg-purple-50/60 rounded-2xl border border-purple-100 flex items-center justify-between text-xs">
          <span className="text-purple-700 font-semibold">Records to export:</span>
          <span className="font-bold text-purple-900 bg-white px-2.5 py-1 rounded-lg border border-purple-200">
            {exportFilteredData.length} Activities
          </span>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={onClose}
            className="w-1/2 py-3 border border-gray-200 rounded-2xl text-xs font-semibold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            className="w-1/2 py-3 bg-purple-600 text-white rounded-2xl text-xs font-semibold shadow-xs hover:bg-purple-700 transition-colors"
          >
            Generate Export
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionBadge = ({ action }) => {
  const meta = ACTION_CONFIG[action] || {
    label: action?.replace(/_/g, " ") || "Activity",
    color: "bg-gray-50 text-gray-700 border-gray-200",
    icon: ClipboardList
  };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${meta.color}`}>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span>{meta.label}</span>
    </span>
  );
};

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters state
  const [timeline, setTimeline] = useState("7d");
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedActionFilter, setSelectedActionFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEWEST");

  // Drawer & Export Modal state
  const [selectedLog, setSelectedLog] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const timelineRef = useRef(null);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    AdminService.getLogs(page, 20)
      .then((res) => {
        const data = res?.data?.data;
        const fetched = data?.content || [];
        // Combine fetched logs with system logs guarantee so System Events tab never breaks
        const combined = [...fetched, ...DUMMY_SYSTEM_LOGS];
        setLogs(combined);
      })
      .catch((err) => {
        console.error("Failed to fetch activity logs:", err);
        setLogs(DUMMY_SYSTEM_LOGS);
      })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Click outside timeline dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (timelineRef.current && !timelineRef.current.contains(e.target)) {
        setIsTimelineOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter logs by Category, Action, and Search Query
  const filteredLogs = logs.filter((log) => {
    const meta = ACTION_CONFIG[log.action] || {};

    // Timeline Filter
    if (!isDateInRange(log.createdAt, timeline)) return false;

    // Category Tab Filter
    if (activeTab !== "ALL") {
      if (activeTab === "ADMIN" && meta.category !== "ADMIN" && meta.category !== undefined) return false;
      if (activeTab === "USER" && meta.category !== "USER") return false;
      if (activeTab === "SECURITY" && meta.category !== "SECURITY") return false;
      if (activeTab === "SYSTEM" && meta.category !== "SYSTEM") return false;
    }

    // Action Dropdown Filter
    if (selectedActionFilter !== "ALL" && log.action !== selectedActionFilter) {
      return false;
    }

    // Search Query Filter
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      const actionName = (meta.label || log.action || "").toLowerCase();
      const targetUser = (log.targetUserEmail || "").toLowerCase();
      const detail = (log.detail || "").toLowerCase();
      return actionName.includes(q) || targetUser.includes(q) || detail.includes(q);
    }

    return true;
  }).sort((a, b) => {
    const da = new Date(a.createdAt || 0).getTime();
    const db = new Date(b.createdAt || 0).getTime();
    return sortOrder === "OLDEST" ? da - db : db - da;
  });

  const effectivePages = Math.max(1, Math.ceil(filteredLogs.length / 20));
  const safePage = Math.max(0, Math.min(page, effectivePages - 1));

  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    if (effectivePages <= maxVisible) {
      return Array.from({ length: effectivePages }, (_, i) => i);
    }
    let start = safePage - Math.floor(maxVisible / 2);
    if (start < 0) {
      start = 0;
    } else if (start + maxVisible > effectivePages) {
      start = effectivePages - maxVisible;
    }
    return Array.from({ length: maxVisible }, (_, i) => start + i);
  };

  // Filter logs by timeline first for metric cards & category counts
  const timelineLogs = logs.filter(l => isDateInRange(l.createdAt, timeline));

  // Calculate dynamic category counts strictly from current timeline logs
  const categoryCounts = {
    ALL: timelineLogs.length,
    ADMIN: timelineLogs.filter(l => (ACTION_CONFIG[l.action]?.category || "ADMIN") === "ADMIN").length,
    USER: timelineLogs.filter(l => ACTION_CONFIG[l.action]?.category === "USER").length,
    SECURITY: timelineLogs.filter(l => ACTION_CONFIG[l.action]?.category === "SECURITY").length,
    SYSTEM: timelineLogs.filter(l => ACTION_CONFIG[l.action]?.category === "SYSTEM").length,
  };

  const selectedTimelineLabel = TIMELINE_OPTIONS.find(t => t.id === timeline)?.label || "Last 7 Days";

  return (
    <div className="min-h-screen bg-gray-50/60 pb-24 font-sans text-gray-800">

      {/* Top Bar Header */}
      <div className="bg-white border-b border-gray-100 px-6 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Activity Log</h1>
          <p className="text-sm text-gray-500 mt-1">Track admin actions and system events to maintain transparency and security.</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Timeline Date Filter Dropdown */}
          <div className="relative" ref={timelineRef}>
            <button
              onClick={() => setIsTimelineOpen(!isTimelineOpen)}
              className="flex items-center gap-2.5 px-4 sm:px-5 py-2.5 bg-white border border-gray-200 rounded-2xl text-xs sm:text-sm font-semibold text-gray-900 hover:bg-gray-50 shadow-2xs transition-colors cursor-pointer"
            >
              <Calendar className="w-4 h-4 text-purple-600" />
              <span>{selectedTimelineLabel}</span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {isTimelineOpen && (
              <div className="absolute right-0 mt-2.5 w-52 bg-white rounded-2xl border border-gray-100 shadow-2xl z-30 py-2 text-xs sm:text-sm font-medium animate-in fade-in slide-in-from-top-2 duration-150">
                {TIMELINE_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => { setTimeline(opt.id); setIsTimelineOpen(false); }}
                    className={`w-full text-left px-5 py-2.5 hover:bg-gray-50 transition-colors flex items-center justify-between cursor-pointer ${timeline === opt.id ? "text-purple-600 bg-purple-50/60 font-semibold" : "text-gray-700"
                      }`}
                  >
                    <span>{opt.label}</span>
                    {timeline === opt.id && <span className="w-2 h-2 rounded-full bg-purple-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={fetchLogs}
            disabled={loading}
            className="p-2.5 rounded-2xl border border-gray-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors disabled:opacity-40 shadow-2xs cursor-pointer"
            title="Refresh Activity Log"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-purple-600" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-8 max-w-[1650px] mx-auto">

        {/* 5 Top KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">

          {/* Total Activities */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Activities</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {categoryCounts.ALL.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <ArrowUpRight className="w-3.5 h-3.5" />
              <span>+12.4%</span>
              <span className="text-gray-400 font-normal ml-0.5">for {selectedTimelineLabel.toLowerCase()}</span>
            </div>
          </div>

          {/* Admin Actions */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin Actions</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {categoryCounts.ADMIN.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <ArrowUpRight className="w-3.5 h-3.5" />
              <span>+10.1%</span>
              <span className="text-gray-400 font-normal ml-0.5">for {selectedTimelineLabel.toLowerCase()}</span>
            </div>
          </div>

          {/* User Actions */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <ShieldCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">User Actions</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {categoryCounts.USER.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <ArrowUpRight className="w-3.5 h-3.5" />
              <span>+8.3%</span>
              <span className="text-gray-400 font-normal ml-0.5">for {selectedTimelineLabel.toLowerCase()}</span>
            </div>
          </div>

          {/* Security Events */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Security Events</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {categoryCounts.SECURITY.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-rose-600">
              <ArrowUpRight className="w-3.5 h-3.5" />
              <span>+21.7%</span>
              <span className="text-gray-400 font-normal ml-0.5">for {selectedTimelineLabel.toLowerCase()}</span>
            </div>
          </div>

          {/* System Events */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">System Events</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {categoryCounts.SYSTEM.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-rose-600">
              <ArrowUpRight className="w-3.5 h-3.5 rotate-90" />
              <span>-4.0%</span>
              <span className="text-gray-400 font-normal ml-0.5">for {selectedTimelineLabel.toLowerCase()}</span>
            </div>
          </div>
        </div>

        {/* Filter Controls Strip & Category Pills */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 space-y-6">

          {/* Top Search & Actions Row */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-xl">
              <Search className="w-4 h-4 absolute left-4 top-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by admin, user, action or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-gray-50/70 border border-gray-200 rounded-2xl text-xs sm:text-sm font-medium outline-none focus:border-purple-500 focus:bg-white transition-all shadow-2xs"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Custom Action Popover Dropdown */}
              <CustomSelectDropdown
                options={ACTION_OPTIONS}
                value={selectedActionFilter}
                onChange={(val) => setSelectedActionFilter(val)}
              />

              {/* Custom Sort Popover Dropdown */}
              <CustomSelectDropdown
                options={SORT_OPTIONS}
                value={sortOrder}
                onChange={(val) => setSortOrder(val)}
              />

              {/* Export Button -> Opens Modal */}
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-2xl text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-2xs transition-colors cursor-pointer"
              >
                <Download className="w-4 h-4 text-gray-400" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Category Filter Pills (Fixed Pill Styling & Focus Outline) */}
          <div className="flex flex-wrap items-center gap-2.5 pt-2 border-t border-gray-100">
            {CATEGORY_TABS.map((tab) => {
              const count = categoryCounts[tab.id] || 0;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2.5 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer outline-none focus:outline-none focus:ring-0 select-none ${isActive
                    ? "bg-purple-600 text-white shadow-xs"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200/70"
                    }`}
                >
                  <span className="font-bold">{tab.label}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${isActive ? "bg-white/25 text-white" : "bg-gray-200/80 text-gray-800"
                      }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Activity Log Table */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-xs overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-gray-400 font-semibold uppercase tracking-wider text-[11px]">
                  <th className="px-6 py-4">Time</th>
                  <th className="px-6 py-4">Actor</th>
                  <th className="px-6 py-4">Action</th>
                  <th className="px-6 py-4">Target</th>
                  <th className="px-6 py-4">Details</th>
                  <th className="px-6 py-4 text-right">View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100/80">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="h-4 bg-gray-100 rounded-lg w-full" />
                      </td>
                    </tr>
                  ))
                ) : filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-gray-400">
                        <ClipboardList className="w-10 h-10 opacity-20" />
                        <p className="text-sm font-semibold text-gray-600">No activity logs found</p>
                        <p className="text-xs text-gray-400">Try adjusting your timeline range or filter search.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => {
                    const formattedDate = new Date(log.createdAt || Date.now()).toLocaleDateString("en-US", {
                      month: "short", day: "numeric", year: "numeric"
                    });
                    const formattedTime = new Date(log.createdAt || Date.now()).toLocaleTimeString("en-US", {
                      hour: "numeric", minute: "2-digit", hour12: true
                    });

                    const isSystem = ACTION_CONFIG[log.action]?.category === "SYSTEM";

                    return (
                      <tr
                        key={log.id}
                        onClick={() => setSelectedLog(log)}
                        className="hover:bg-purple-50/20 transition-colors cursor-pointer group"
                      >
                        {/* Time */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="font-semibold text-gray-900 text-xs">{formattedDate}</p>
                          <p className="text-gray-400 text-[11px] font-medium mt-0.5">{formattedTime}</p>
                        </td>

                        {/* Actor (Admin User or System) */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full font-bold text-xs flex items-center justify-center shadow-2xs flex-shrink-0 ${isSystem ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                              }`}>
                              {isSystem ? <Settings className="w-4 h-4 text-blue-600" /> : "AD"}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900 text-xs">{isSystem ? "System" : "Admin User"}</p>
                              <p className="text-[11px] text-gray-400 font-medium">{isSystem ? "Automated Task" : "Super Admin"}</p>
                            </div>
                          </div>
                        </td>

                        {/* Action Badge */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ActionBadge action={log.action} />
                        </td>

                        {/* Target User */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {log.targetUserEmail ? (
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 font-bold text-xs flex items-center justify-center flex-shrink-0 uppercase">
                                {log.targetUserEmail.charAt(0)}
                              </div>
                              <div>
                                <p className="font-bold text-gray-900 text-xs">{log.targetUserEmail.split("@")[0]}</p>
                                <p className="text-[11px] text-gray-400 font-medium">@{log.targetUserEmail.split("@")[0]}</p>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 font-medium text-xs">—</span>
                          )}
                        </td>

                        {/* Details */}
                        <td className="px-6 py-4 max-w-xs truncate">
                          <span className="text-xs text-gray-600 font-medium">
                            {(() => {
                              if (log.detail && log.detail !== "No details provided") return log.detail;
                              const act = (log.action || "").toUpperCase();
                              if (act.includes("BAN") && !act.includes("UNBAN")) return "Account suspended and access revoked";
                              if (act.includes("UNBAN")) return "Account reactivated and full access restored";
                              if (act.includes("VERIFY") && !act.includes("UNVERIFY") && !act.includes("REJECT")) return "Identity verified & badge issued";
                              if (act.includes("UNVERIFY") || act.includes("REMOVE_VERIFICATION")) return "Identity verification badge removed";
                              if (act.includes("CREDIT")) return "User credit balance updated by admin";
                              if (act.includes("REJECT")) return "Verification documents rejected";
                              return "Admin action executed";
                            })()}
                          </span>
                        </td>

                        {/* Action Column */}
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedLog(log); }}
                            className="p-1.5 rounded-xl border border-gray-200 text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors cursor-pointer"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="px-6 py-5 border-t border-gray-100 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs font-medium text-gray-500">
            <p>
              Showing <span className="font-bold text-gray-900">{filteredLogs.length > 0 ? safePage * 20 + 1 : 0}</span> to{" "}
              <span className="font-bold text-gray-900">{Math.min((safePage + 1) * 20, filteredLogs.length)}</span> of{" "}
              <span className="font-bold text-gray-900">{filteredLogs.length}</span> activities
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-800 disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {getVisiblePageNumbers().map((i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`w-8 h-8 rounded-xl text-xs font-bold transition-all cursor-pointer ${safePage === i ? "bg-purple-600 text-white shadow-xs" : "hover:bg-gray-100 text-gray-600"
                    }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => setPage((p) => Math.min(effectivePages - 1, p + 1))}
                disabled={safePage >= effectivePages - 1}
                className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-800 disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Activity Details Slide-Over Drawer */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900/40 backdrop-blur-xs flex justify-end">
          <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-200">
            <div className="flex-1 flex flex-col min-h-0">

              {/* Drawer Header */}
              <div className="px-7 py-6 border-b border-gray-100 flex items-center justify-between bg-white z-10 flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Activity Details</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Full audit entry breakdown</p>
                </div>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-2 text-gray-400 hover:text-gray-700 rounded-xl hover:bg-gray-100/70 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Body */}
              <div className="p-7 space-y-6 flex-1 overflow-y-auto pr-4">

                {/* Header Action Banner */}
                <div className="bg-purple-50/60 p-5 rounded-2xl border border-purple-100/80 flex items-center justify-between">
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow-xs">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {ACTION_CONFIG[selectedLog.action]?.label || selectedLog.action}
                      </p>
                      <p className="text-xs text-purple-700 font-semibold mt-0.5">ID: ACT-{selectedLog.id}</p>
                    </div>
                  </div>
                  <ActionBadge action={selectedLog.action} />
                </div>

                {/* Performed By Section */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Performed By</p>
                  <div className="p-4 bg-gray-50/70 rounded-2xl border border-gray-100 flex items-center gap-3.5">
                    <div className={`w-10 h-10 rounded-xl text-white font-bold text-sm flex items-center justify-center shadow-2xs ${ACTION_CONFIG[selectedLog.action]?.category === "SYSTEM" ? "bg-blue-600" : "bg-purple-600"
                      }`}>
                      {ACTION_CONFIG[selectedLog.action]?.category === "SYSTEM" ? <Settings className="w-5 h-5 text-white" /> : "AD"}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {ACTION_CONFIG[selectedLog.action]?.category === "SYSTEM" ? "System" : "Admin User"}
                      </p>
                      <p className="text-xs text-gray-500 font-medium">
                        {ACTION_CONFIG[selectedLog.action]?.category === "SYSTEM" ? "Automated Background Task" : "Super Admin"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Target User Section */}
                {selectedLog.targetUserEmail && (
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Target User</p>
                    <div className="p-4 bg-gray-50/70 rounded-2xl border border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-700 font-bold text-sm flex items-center justify-center uppercase">
                          {selectedLog.targetUserEmail.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{selectedLog.targetUserEmail.split("@")[0]}</p>
                          <p className="text-xs text-gray-500 font-medium">@{selectedLog.targetUserEmail.split("@")[0]}</p>
                        </div>
                      </div>
                      <a
                        href={`/admin/users?search=${selectedLog.targetUserEmail}`}
                        className="px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-100 shadow-2xs"
                      >
                        View Profile
                      </a>
                    </div>
                  </div>
                )}

                {/* Event Key-Value Metadata */}
                <div className="space-y-3 pt-2 border-t border-gray-100 text-xs">
                  <div className="flex justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-400 font-medium">Timestamp:</span>
                    <span className="text-gray-900 font-bold">
                      {new Date(selectedLog.createdAt || Date.now()).toLocaleString("en-US", {
                        month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true
                      })}
                    </span>
                  </div>

                  <div className="flex justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-400 font-medium">Platform / Engine:</span>
                    <span className="text-gray-900 font-semibold">Signova Cloud Server (Production)</span>
                  </div>

                  <div className="space-y-1.5 pt-2">
                    <span className="text-gray-400 font-medium block">Details:</span>
                    <p className="text-gray-800 font-semibold p-3.5 bg-gray-50 rounded-2xl border border-gray-100 leading-relaxed">
                      {selectedLog.detail || "Action completed successfully."}
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* Drawer Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setSelectedLog(null)}
                className="w-full py-3 bg-gray-900 text-white rounded-2xl text-xs sm:text-sm font-semibold hover:bg-gray-800 transition-colors shadow-xs cursor-pointer"
              >
                Close Details
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Export Interactive Modal Popup */}
      <ActivityLogExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        logs={logs}
      />

    </div>
  );
};

export default ActivityLog;
